import { useEffect, useMemo, useState } from "react";
import {
  Star,
  X,
  Filter,
  CheckCircle,
  AlertCircle,
} from "lucide-react";
import "./admin-reviews.css";
import { HashLoader } from "react-spinners";
import ToastNotification, {
  showToast,
} from "../../components/toasts/ToastNotification";
import {
  deleteReviewForAdmin,
  getAllReviewsForAdmin,
  updateReviewStatusForAdmin,
} from "../../api/review.api";
import { GrFormPrevious, GrFormNext } from "react-icons/gr";
import { MdDelete } from "react-icons/md";
import { IoMdMail } from "react-icons/io";
import ConfirmModal from "../../components/modal/confirm-modal";
import { useUser } from "../../context/UserContext";
import { hasPermission } from "../../utils/auth";

const ITEMS_PER_PAGE = 5;

export default function AdminReviews() {
  const [reviews, setReviews] = useState([]);
  const [filterType, setFilterType] = useState("all");
  const [statusFilter, setStatusFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [loading, setLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [selectedIdToDelete, setSelectedIdToDelete] = useState("");
  const { user } = useUser();

  const fetchReviews = async () => {
    setLoading(true);
    try {
      const res = await getAllReviewsForAdmin(ITEMS_PER_PAGE, currentPage);
      const data = res.data;
      setReviews(
        data.reviews.map((r) => ({
          id: r._id,
          username: r.userId?.username || "Người dùng không xác định",
          avatar: r.userId?.profileImg || "/api/placeholder/40/40",
          rating: r.rating,
          comment: r.comment,
          time: r.createdAt,
          orderId: r.orderId,
          status: r.status || "show",
        }))
      );
      setTotalPages(data.totalPages || 1);
    } catch (error) {
      showToast(error.message || "Failed to load reviews", "error");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchReviews();
    document.title = 'Admin - Đánh giá';
  }, [currentPage]);

  const formatDate = (dateString) => {
    const options = {
      year: "numeric",
      month: "short",
      day: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    };
    return new Date(dateString).toLocaleDateString("vi-VN", options);
  };

  const changeStatus = async (id, newStatus) => {
    setLoading(true);

    try {
      const res = await updateReviewStatusForAdmin(id, newStatus);

      if (res?.success) {
        setReviews(
          reviews.map((review) =>
            review.id === res.data.updatedReview?._id ? { ...review, status: res.data.updatedReview?.status } : review
          )
          );
          
          showToast("Cập nhật trạng thái thành công", "success");
      }
    } catch (error) {
      showToast(error.message, "error");
    } finally {
      setLoading(false);
    }
  };

  const filteredReviews = () => {
    let filtered = reviews;

    switch (filterType) {
      case "high":
        filtered = filtered.filter((review) => review.rating >= 4);
        break;
      case "low":
        filtered = filtered.filter((review) => review.rating <= 2);
        break;
      default:
        break;
    }

    if (statusFilter !== "all") {
      filtered = filtered.filter((review) => review.status === statusFilter);
    }

    return filtered;
  };

  const renderStars = (rating) => {
    return Array(5)
      .fill(0)
      .map((_, i) => (
        <Star
          key={i}
          size={16}
          fill={i < rating ? "#FFD700" : "none"}
          stroke={i < rating ? "#FFD700" : "#ccc"}
        />
      ));
  };

  const renderStatusIcon = (status) => {
    switch (status) {
      case "show":
        return (
          <CheckCircle
            size={16}
            className="admin-review-status-icon admin-review-show"
          />
        );
      case "hide":
        return (
          <X size={16} className="admin-review-status-icon admin-review-hide" />
        );
      default:
        return null;
    }
  };

  const getStatusLabel = (status) => {
    switch (status) {
      case "show":
        return "Hiển thị";
      case "hide":
        return "Bị ẩn";
      default:
        return "";
    }
  };

  const handlePrevPage = () => {
    if (currentPage > 1) setCurrentPage(currentPage - 1);
  };

  const handleNextPage = () => {
    if (currentPage < totalPages) setCurrentPage(currentPage + 1);
  };

  const handleFilterChange = (type, value) => {
    if (type === "type") setFilterType(value);
    else setStatusFilter(value);
    setCurrentPage(1); // reset page when filters change
  };


  const handleDeleteReview = async () => {
    setLoading(true);
    setIsModalOpen(false);

    try {
      const res = await deleteReviewForAdmin(selectedIdToDelete);

      if (res.success) {
        showToast("Xóa đánh giá thành công", "success");

        fetchReviews();
      }
    } catch (error) {
      showToast(error.message, "error");
    }
    finally {
      setLoading(false);
    }
  }

  const visibleReviews = filteredReviews();
  const summary = useMemo(() => {
    return reviews.reduce(
      (acc, review) => {
        acc.total += 1;
        if (review.status === "show") acc.show += 1;
        if (review.status === "hide") acc.hide += 1;
        if (review.rating >= 4) acc.positive += 1;
        return acc;
      },
      { total: 0, show: 0, hide: 0, positive: 0 }
    );
  }, [reviews]);

  if (loading) {
    return (
      <div className="loader-container">
        <HashLoader color="var(--hash-loader-color)" />
      </div>
    );
  }

  return (
    <section className="admin-reviews-page">
      <ToastNotification />
      <ConfirmModal
        isOpen={isModalOpen}
        onConfirm={handleDeleteReview}
        onClose={() => setIsModalOpen(false)}
        message={"Xác nhận bạn đang xóa một đánh giá của khách hàng"}
        title={"Xóa đánh giá"}
      />
      <div className="admin-reviews-shell">
        <header className="admin-reviews-head">
          <div>
            <p className="admin-reviews-breadcrumb">Quản trị / Đánh giá</p>
            <h1 className="admin-reviews-title">Đánh giá khách hàng</h1>
          </div>
          <div className="admin-reviews-kpi">
            <div className="admin-reviews-kpi-item">
              <span>Tổng đánh giá</span>
              <strong>{summary.total}</strong>
            </div>
            <div className="admin-reviews-kpi-item">
              <span>Đang hiển thị</span>
              <strong>{summary.show}</strong>
            </div>
            <div className="admin-reviews-kpi-item">
              <span>Đang ẩn</span>
              <strong>{summary.hide}</strong>
            </div>
            <div className="admin-reviews-kpi-item">
              <span>Tích cực (4-5 sao)</span>
              <strong>{summary.positive}</strong>
            </div>
          </div>
        </header>

        <div className="admin-reviews-toolbar">
          <div className="admin-reviews-filter-group">
            <Filter size={16} />
            <select
              value={filterType}
              onChange={(e) => handleFilterChange("type", e.target.value)}
              className="admin-reviews-select"
            >
              <option value="all">Tất cả</option>
              <option value="high">Đánh giá cao (4-5)</option>
              <option value="low">Đánh giá thấp (1-2)</option>
            </select>
          </div>

          <div className="admin-reviews-filter-group">
            <AlertCircle size={16} />
            <select
              value={statusFilter}
              onChange={(e) => handleFilterChange("status", e.target.value)}
              className="admin-reviews-select"
            >
              <option value="all">Tất cả trạng thái</option>
              <option value="show">Hiển thị</option>
              <option value="hide">Bị ẩn</option>
            </select>
          </div>

          <p className="admin-reviews-result-text">
            Hiển thị {visibleReviews.length} đánh giá trong trang {currentPage}
          </p>
        </div>

        <div className="admin-reviews-list">
        {visibleReviews.map((review) => (
          <div
            key={review.id}
            className="admin-reviews-card"
          >
            <div className="admin-reviews-card-head">
              <div className="admin-reviews-user">
                <img
                  src={review.avatar}
                  alt={review.username}
                  className="admin-reviews-avatar"
                />
                <span className="admin-reviews-username">{review.username}</span>
              </div>
              <div className="admin-reviews-actions">
                {hasPermission(user?.role, "delete:comment") &&
                <button
                  className="admin-reviews-delete-btn"
                  onClick={() => {
                    setSelectedIdToDelete(review.id);
                    setIsModalOpen(true);
                  }}
                >
                  <MdDelete
                    className="delete-icon-button"
                    size={16}
                    stroke="#ff4d4d"
                  />
                </button>}
              </div>
            </div>

            <div className="admin-reviews-rating-row">
              <div className="admin-reviews-stars">
                {renderStars(review.rating)}
              </div>
              <span className="admin-reviews-time">
                {formatDate(review.time)}
              </span>
            </div>

            <p className="admin-reviews-comment">{review.comment}</p>

            <div className="admin-reviews-footer">
              <div className="admin-reviews-order-info">
                <IoMdMail size={14} />
                <span>Đánh giá từ đơn hàng <span className="admin-reviews-order-id">#{review.orderId}</span></span>
              </div>

              <div className="admin-reviews-status-wrap">
                <div className="admin-reviews-status-indicator">
                  {renderStatusIcon(review.status)}
                  <span
                    className={`admin-reviews-status-badge ${review.status === "show" ? "is-show" : "is-hide"}`}
                  >
                    {getStatusLabel(review.status)}
                  </span>
                </div>
                {hasPermission(user?.role, "switch_status:comment") &&
                <div className="admin-reviews-status-actions">
                  <button
                    className={`admin-reviews-status-btn ${
                      review.status === "show" ? "is-active" : ""
                    }`}
                    onClick={() => changeStatus(review.id, "show")}
                  >
                    Hiển thị
                  </button>
                  <button
                    className={`admin-reviews-status-btn is-hide ${
                      review.status === "hide" ? "is-active" : ""
                    }`}
                    onClick={() => changeStatus(review.id, "hide")}
                  >
                    Ẩn
                  </button>
                </div>}
              </div>
            </div>
          </div>
        ))}
        </div>

      {/* Pagination Controls */}
      <div className="admin-reviews-pagination-wrap">
        {totalPages > 1 && (
        <div className="admin-reviews-pagination">
          <button
            className="admin-reviews-pagination-btn"
            onClick={handlePrevPage}
            disabled={currentPage === 1}
          >
            <GrFormPrevious className="admin-reviews-pagination-icon" />
          </button>
          <span className="admin-reviews-pagination-info">
            <span className="admin-reviews-pagination-text">
              {currentPage}/{totalPages}
            </span>
          </span>
          <button
            className="admin-reviews-pagination-btn"
            onClick={handleNextPage}
            disabled={currentPage === totalPages}
          >
            <GrFormNext className="admin-reviews-pagination-icon" />
          </button>
        </div>
      )}
      </div>
      </div>
    </section>
  );
}
